import type { CSSProperties } from 'react';
import { hhmm, isTeams } from '@jobfair/shared';
import { copyText, heDate } from '../lib/format';
import { useDialog } from '../lib/hooks';
import { toast } from '../lib/toast';
import { useTrack } from '../lib/trackerContext';
import type { LiveState, Phase, Site } from '../types';
import { ExtLink } from './common';
import { Icon } from './Icon';

type Consult = Site['consult'];

export const consultOpen = (c: Consult, ph: Phase): boolean =>
  c.open === 'always'
    ? ph.state !== 'ended'
    : c.open === 'sessions'
      ? ph.state === 'sessions'
      : ph.state === 'keynote' || ph.state === 'sessions';

const consultWhen = (c: Consult, st: LiveState, ph: Phase): string => {
  const r1 = ph.valid ? hhmm(ph.R[0]!.start) : st.rounds[0].start;
  return c.open === 'sessions' ? r1 : st.convStart;
};

function ConsultArt() {
  return (
    <svg className="c-art" viewBox="0 0 160 136" aria-hidden="true" focusable="false">
      <g className="b1">
        <path
          d="M18 14h86a14 14 0 0 1 14 14v38a14 14 0 0 1-14 14H58L36 98V80H18A14 14 0 0 1 4 66V28a14 14 0 0 1 14-14z"
          fill="#112752"
        />
        <text
          x="61"
          y="63"
          textAnchor="middle"
          fontSize="40"
          fontWeight="500"
          fill="#d4b25e"
          fontFamily="Rubik,Arial,sans-serif"
        >
          ?
        </text>
      </g>
      <g className="b2">
        <path
          d="M80 66h62a12 12 0 0 1 12 12v24a12 12 0 0 1-12 12h-8v16l-18-16H80a12 12 0 0 1-12-12V78a12 12 0 0 1 12-12z"
          fill="var(--surface)"
          stroke="#b8952a"
          strokeWidth="2"
        />
        <circle cx="97" cy="90" r="4" fill="#b8952a" />
        <circle cx="111" cy="90" r="4" fill="#b8952a" />
        <circle cx="125" cy="90" r="4" fill="#b8952a" />
      </g>
    </svg>
  );
}

export function ConsultBooth({
  site,
  st,
  ph,
  onOpen,
}: {
  site: Site;
  st: LiveState;
  ph: Phase;
  onOpen: () => void;
}) {
  const track = useTrack();
  const c = site.consult;
  if (!c.enabled) return null;
  const open = consultOpen(c, ph);
  const valid = isTeams(c.teams);
  const live = open && valid;
  const when =
    ph.state === 'ended'
      ? 'העמדה נסגרה'
      : open
        ? valid
          ? 'פתוחה עכשיו'
          : 'הקישור יפורסם בקרוב'
        : `תיפתח ב-${consultWhen(c, st, ph)}`;

  return (
    <section className="wrap consult-wrap" aria-labelledby="consult-h">
      <div className="consult">
        <ConsultArt />
        <div className="c-main">
          <span className={'c-eyebrow' + (live ? ' on' : '')}>
            {live ? <span className="live-dot" /> : <Icon n="chat" s={15} />}
            עמדת ייעוץ · {when}
          </span>
          <h2 id="consult-h">{c.title}</h2>
          {c.text && <p>{c.text}</p>}
          <div className="c-actions">
            {live && (
              <ExtLink
                className="btn btn-consult"
                href={c.teams}
                label="עמדת הייעוץ של קודקס"
                onOpen={() => track('consult_join', { value: 'booth' })}
              >
                <Icon n="video" s={17} />
                הצטרפות לעמדת הייעוץ
              </ExtLink>
            )}
            <button
              className="btn btn-ghost"
              onClick={() => {
                track('consult_open');
                onOpen();
              }}
              aria-haspopup="dialog"
            >
              <Icon n="help" s={17} />
              {c.faq.length ? `שאלות נפוצות (${c.faq.length})` : 'פרטים ויצירת קשר'}
            </button>
            {c.faqPdf?.url && (
              <ExtLink
                className="btn btn-quiet"
                href={c.faqPdf.url}
                label="המסמך"
                onOpen={() => track('pdf_open', { value: 'consult-faq' })}
              >
                <Icon n="file" s={17} />
                {c.faqPdf.title || 'שאלות ותשובות (PDF)'}
              </ExtLink>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({
  icon,
  value,
  label,
}: {
  icon: 'mail' | 'call';
  value: string;
  label: string;
}) {
  return (
    <div className="contact-row">
      <Icon n={icon} s={18} />
      <span className="v">{value}</span>
      <button
        className="btn btn-quiet btn-sm"
        onClick={async () =>
          (await copyText(value))
            ? toast(`${label} הועתק`, 'ok')
            : toast('סמנו את הטקסט והעתיקו ידנית', 'info')
        }
      >
        <Icon n="copy" s={15} />
        העתקה
      </button>
    </div>
  );
}

export function ConsultDrawer({
  site,
  st,
  ph,
  onClose,
}: {
  site: Site;
  st: LiveState;
  ph: Phase;
  onClose: () => void;
}) {
  const ref = useDialog<HTMLDivElement>(onClose);
  const track = useTrack();
  const c = site.consult;
  const open = consultOpen(c, ph);
  const valid = isTeams(c.teams);

  let join;
  if (open && valid) {
    join = (
      <div className="join-box">
        <ExtLink
          className="btn btn-consult btn-lg"
          href={c.teams}
          label="עמדת הייעוץ של קודקס"
          onOpen={() => track('consult_join', { value: 'drawer' })}
        >
          <Icon n="video" s={20} />
          הצטרפות לעמדת הייעוץ
        </ExtLink>
        <small>השיחה תיפתח ב-Microsoft Teams בלשונית חדשה, ואפשר לחזור לכאן בכל רגע.</small>
      </div>
    );
  } else if (open) {
    join = (
      <div className="callout info">
        <Icon n="info" />
        <span>העמדה פתוחה, והקישור לשיחה יפורסם כאן בקרוב.</span>
      </div>
    );
  } else if (ph.state === 'ended') {
    join = (
      <div className="callout info">
        <Icon n="check" />
        <span>עמדת הייעוץ נסגרה. אפשר לפנות לקודקס בפרטי הקשר שלמטה.</span>
      </div>
    );
  } else {
    join = (
      <div className="callout info">
        <Icon n="clock" />
        <span>
          עמדת הייעוץ תיפתח ב-{heDate(st.date, { day: 'numeric', month: 'long' })} בשעה{' '}
          <b className="num">{consultWhen(c, st, ph)}</b>.
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="c-title"
        ref={ref}
        tabIndex={-1}
        style={{ '--ac': 'var(--c2)' } as CSSProperties}
      >
        <div className="drawer-head">
          <span className="small muted">עמדת ייעוץ</span>
          <button className="icon-btn" onClick={onClose} aria-label="סגירה" data-autofocus>
            <Icon n="close" />
          </button>
        </div>
        <div className="drawer-body">
          <div className="d-band backdrop p2" aria-hidden="true">
            <span className="emblem lg">
              <Icon n="chat" s={30} />
            </span>
          </div>
          <div className="d-titlewrap">
            <h2 className="d-title" id="c-title">
              {c.title}
            </h2>
            {c.text && <p className="desc">{c.text}</p>}
          </div>
          {join}
          {c.faq.length > 0 && (
            <section>
              <div className="sec-label">שאלות נפוצות</div>
              <div className="faq">
                {c.faq.map((f, i) => (
                  <details key={i} open={i === 0}>
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
          {c.faqPdf?.url && (
            <section>
              <div className="res-list">
                <ExtLink
                  className="res"
                  href={c.faqPdf.url}
                  label="המסמך"
                  onOpen={() => track('pdf_open', { value: 'consult-faq' })}
                >
                  <span className="ri pdf">
                    <Icon n="file" />
                  </span>
                  <span>
                    <b>{c.faqPdf.title || 'שאלות ותשובות'}</b>
                    <small>PDF · נפתח בלשונית חדשה</small>
                  </span>
                  <span className="ext">
                    <Icon n="ext" s={16} />
                  </span>
                </ExtLink>
              </div>
            </section>
          )}
          {(c.email || c.phone) && (
            <section>
              <div className="sec-label">יצירת קשר עם קודקס</div>
              <div className="contact">
                {c.email && <ContactRow icon="mail" value={c.email} label="כתובת המייל" />}
                {c.phone && <ContactRow icon="call" value={c.phone} label="מספר הטלפון" />}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
